import React, { useEffect, useState } from "react";
import {
  FieldTypeType,
  OptionType,
  SubOptionType,
} from "../../../services/helpers/types";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import {
  sortableKeyboardCoordinates,
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import DraggableSelectOptionsItem from "../DraggableSelectOptionsItem/DraggableSelectOptionsItem";

interface DraggableSelectOptionsListProps {
  type?: FieldTypeType;
  options: OptionType[];
  onOptionChange: (option: OptionType) => void;
  onDeleteItem: (itemID: number) => void;
  onChangePosition: (newItemsPositions: number[]) => void;
  onCreateSubOption?: (options?: OptionType) => void;
  onSubOptionsChange?: (option: SubOptionType) => void;
}

const DraggableSelectOptionsList = (props: DraggableSelectOptionsListProps) => {
  const {
    type = FieldTypeType.Select,
    options,
    onDeleteItem,
    onOptionChange,
    onChangePosition,
    onCreateSubOption,
    onSubOptionsChange,
  } = props;
  const [items, setItems] = useState<number[]>([]);

  const [subOptionsVisible, setSubOptionsVisible] = useState(true);

  useEffect(() => {
    const optionPositions = options.map((option) => option.position);
    setItems(optionPositions);
  }, [options]);

  const updatedItems = (items: number[], active: any, over: any) => {
    const oldIndex = items.indexOf(active.id);
    const newIndex = items.indexOf(over.id);

    return arrayMove(items, oldIndex, newIndex);
  };
  const handleDragEnd = (event: any) => {
    setSubOptionsVisible(true);
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const newItems = updatedItems(items, active, over);

      setItems(newItems);
      onChangePosition(newItems);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor)
  );

  const handleDragStart = () => {
    setSubOptionsVisible(false);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((position) => (
          <DraggableSelectOptionsItem
            subOptionsVisible={subOptionsVisible}
            type={type}
            options={options}
            key={position}
            id={position}
            option={options.find((item) => item.position === position)}
            onDelete={onDeleteItem}
            onOptionChange={onOptionChange}
            onCreateSubOption={onCreateSubOption}
            onSubOptionsChange={onSubOptionsChange}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default DraggableSelectOptionsList;
